import { defineFont } from '@progress/kendo-drawing/pdf';

// Single source of truth for E-Letter PDF export geometry.
// Preview pagination (ELetterPreview auto page-break / bottom anchoring) and
// the Kendo drawDOM export must both derive from these values so the
// downloaded PDF matches the preview.

// Default template content width (ELetterPreview forces #pdf-data-print to
// 850px). Real templates can be wider - a content-box root of width:850px
// with padding:60px renders 970px - so scale and page capacity derive from
// the MEASURED template width, with this as the fallback.
export const TEMPLATE_CONTENT_WIDTH_PX = 850;

// A4 in PDF points and the printed page margin
const A4_WIDTH_PT = 595;
const A4_HEIGHT_PT = 842;
const PAGE_MARGIN = '5mm';
const PAGE_MARGIN_PT = 14;

const PRINTABLE_WIDTH_PT = A4_WIDTH_PT - 2 * PAGE_MARGIN_PT;
const PRINTABLE_HEIGHT_PT = A4_HEIGHT_PT - 2 * PAGE_MARGIN_PT;

// Scale that maps a template of the given width 1:1 onto the printable A4 width
export const getExportScale = (templateWidthPx?: number) =>
  Math.floor(
    (PRINTABLE_WIDTH_PT /
      Math.max(templateWidthPx || TEMPLATE_CONTENT_WIDTH_PX, 1)) *
      1000,
  ) / 1000;

// Content pixels one exported A4 page holds for a template of the given width -
// the scale cancels out, leaving the printable-area aspect ratio
export const getPageContentHeightPx = (templateWidthPx?: number) =>
  Math.floor(
    ((templateWidthPx || TEMPLATE_CONTENT_WIDTH_PX) * PRINTABLE_HEIGHT_PT) /
      PRINTABLE_WIDTH_PT,
  );

// Fixed-width equivalents for callers without a measured template width
export const EXPORT_PDF_SCALE = getExportScale(TEMPLATE_CONTENT_WIDTH_PX);
export const A4_CONTENT_HEIGHT_PX = getPageContentHeightPx(
  TEMPLATE_CONTENT_WIDTH_PX,
);
export const A4_CONTENT_WIDTH_PX = Math.floor(
  PRINTABLE_WIDTH_PT / EXPORT_PDF_SCALE,
);

// CX-5430: the Kendo PDF writer has no per-character font fallback like the
// browser - text in a family it does not know degrades to the built-in
// Helvetica (WinAnsi), which turns CJK characters into garbage glyphs. Map
// the families templates commonly use to a bundled CJK-capable font so the
// export embeds real glyphs (Kendo subsets the font per document). Latin
// text in these exports renders in Noto Sans instead of Helvetica.
let pdfFontsRegistered = false;
export const registerPdfExportFonts = () => {
  if (pdfFontsRegistered) return;
  pdfFontsRegistered = true;
  const base = `${process.env.PUBLIC_URL || ''}/fonts`;
  const regular = `${base}/NotoSansSC-Regular.ttf`;
  const bold = `${base}/NotoSansSC-Bold.ttf`;
  const families = [
    'Segoe UI',
    'Tahoma',
    'Geneva',
    'Verdana',
    'Arial',
    'Helvetica',
    'Times New Roman',
    'Microsoft YaHei',
    'SimSun',
    'SimHei',
    'sans-serif',
    'serif',
  ];
  const definitions: { [fontName: string]: string } = {};
  families.forEach(family => {
    definitions[family] = regular;
    definitions[`${family}|Bold`] = bold;
    definitions[`${family}|Italic`] = regular;
    definitions[`${family}|Bold Italic`] = bold;
  });
  defineFont(definitions);
};

// CX-5430: footer block to bottom-anchor on a page. Templates are free-form:
// some only have #ItemFooter, and some nest #PageFooter INSIDE #ContentFooter
// (last line of the block) - picking the inner one there would split the
// block, so return the OUTERMOST footer element that exists
export const findFooterBlock = (root: Element): HTMLElement | null => {
  const candidates = ['#PageFooter', '#ContentFooter', '#ItemFooter']
    .map(selector => root.querySelector(selector) as HTMLElement | null)
    .filter((el): el is HTMLElement => !!el);
  return (
    candidates.find(el =>
      candidates.every(other => other === el || !other.contains(el)),
    ) ??
    candidates[0] ??
    null
  );
};

// CX-5430: a designer-inserted <!-- pagebreak --> INSIDE the footer block
// moves everything from the marker onward onto its own printed page (e.g.
// remarks + signatory after the totals). Returns the footer's direct child
// where the break begins, or null when no marker has real content after it
// (a trailing marker would only add a blank page).
export const findFooterBreakChild = (footerEl: Element): Element | null => {
  const containsMarker = (node: Node): boolean =>
    (node.nodeType === Node.COMMENT_NODE &&
      (node.textContent || '').trim() === 'pagebreak') ||
    Array.from(node.childNodes).some(containsMarker);
  const hasContent = (els: Element[]) =>
    els.some(
      el =>
        (el.textContent || '').replace(/\u00a0/g, ' ').trim() !== '' ||
        !!el.querySelector('img, table'),
    );
  const children = Array.from(footerEl.children);
  for (let i = 0; i < children.length; i++) {
    if (!containsMarker(children[i])) continue;
    if (hasContent(children.slice(i))) return children[i];
  }
  return null;
};

// Whether a template's footer carries a manual page break worth honouring
export const templateHasFooterBreak = (
  templateHtml?: string | null,
): boolean => {
  if (!templateHtml || typeof DOMParser === 'undefined') return false;
  const doc = new DOMParser().parseFromString(templateHtml, 'text/html');
  const footerEl = findFooterBlock(doc.body);
  return !!footerEl && !!findFooterBreakChild(footerEl);
};

// CX-5430: some templates (IAQ) draw the column headers in a SEPARATE
// #ItemLabel table above the #ItemValue row table(s). Two hand-maintained
// tables never agree on column widths, so the item columns drift from the
// header. Copy the header table's declared column widths onto every item
// table as a shared colgroup; when the item row has FEWER cells than the
// header (blank spacer columns merged away), spacer widths are summed into
// the preceding real column so the boundaries still coincide.
const resolveTable = (el: Element | null): HTMLTableElement | null => {
  if (!el) return null;
  if (el.tagName === 'TABLE') return el as HTMLTableElement;
  return (el.closest('table') ??
    el.querySelector('table')) as HTMLTableElement | null;
};

const rowCells = (row: Element): HTMLTableCellElement[] =>
  Array.from(row.children).filter(
    (c): c is HTMLTableCellElement =>
      c.tagName === 'TD' || c.tagName === 'TH',
  );

// Declared column widths, percent units only: colgroup first, else the
// first row's cell widths. Null when any column is unparseable or spanned.
const declaredColumnWidths = (table: HTMLTableElement): number[] | null => {
  const cols = Array.from(
    table.querySelector('colgroup')?.querySelectorAll('col') ?? [],
  );
  let sources: string[];
  if (cols.length > 0) {
    sources = cols.map(c => (c as HTMLElement).style.width || '');
  } else {
    const firstRow = table.querySelector('tr');
    if (!firstRow) return null;
    const cells = rowCells(firstRow);
    if (cells.some(c => c.colSpan > 1)) return null;
    sources = cells.map(c => c.style.width || '');
  }
  const widths = sources.map(w =>
    /^\d*\.?\d+%$/.test(w.trim()) ? parseFloat(w) : NaN,
  );
  if (widths.length === 0 || widths.some(w => !isFinite(w) || w <= 0)) {
    return null;
  }
  return widths;
};

// Sum each blank spacer column's width into the preceding real column
const mergeSpacerWidths = (
  headerTable: HTMLTableElement,
  widths: number[],
): number[] | null => {
  const headerRow = headerTable.querySelector('tr');
  if (!headerRow) return null;
  const cells = rowCells(headerRow);
  if (cells.length !== widths.length || cells.some(c => c.colSpan > 1)) {
    return null;
  }
  const groups: number[] = [];
  cells.forEach((cell, i) => {
    const blank =
      (cell.textContent || '').replace(/\u00a0/g, ' ').trim() === '';
    if (!blank || groups.length === 0) groups.push(widths[i]);
    else groups[groups.length - 1] += widths[i];
  });
  return groups;
};

export const alignSeparateItemTables = (root: HTMLElement) => {
  const labelTable = resolveTable(root.querySelector('#ItemLabel'));
  const itemTables = Array.from(root.querySelectorAll('[id="ItemValue"]'))
    .map(el => resolveTable(el))
    .filter(
      (t, i, arr): t is HTMLTableElement => !!t && arr.indexOf(t) === i,
    );
  // single-table templates (header rows and #ItemValue in one table) align
  // themselves - only the separate-table shape needs the sync
  if (!labelTable || itemTables.length === 0) return;
  if (itemTables.some(t => t === labelTable || labelTable.contains(t))) return;

  const rawWidths = declaredColumnWidths(labelTable);
  if (!rawWidths) return;
  const mergedWidths = mergeSpacerWidths(labelTable, rawWidths);

  const syncedTables: Array<{
    table: HTMLTableElement;
    target: number[];
  }> = [];
  itemTables.forEach(table => {
    const firstRow = table.querySelector('tr');
    const cellCount = firstRow ? rowCells(firstRow).length : 0;
    const target =
      cellCount === rawWidths.length
        ? rawWidths
        : mergedWidths && cellCount === mergedWidths.length
        ? mergedWidths
        : null;
    if (!target) return;
    table.querySelectorAll('colgroup').forEach(cg => cg.remove());
    const colgroup = table.ownerDocument!.createElement('colgroup');
    target.forEach(w => {
      const col = table.ownerDocument!.createElement('col');
      col.style.width = `${w}%`;
      colgroup.appendChild(col);
    });
    table.insertBefore(colgroup, table.firstChild);
    table.style.tableLayout = 'fixed';
    // Chrome resolves a col-vs-cell width conflict in the cells' favour,
    // so the template's own td widths would still win - stamp the target
    // widths on every same-shaped row so both sources agree
    Array.from(table.querySelectorAll('tr')).forEach(tr => {
      const cells = rowCells(tr);
      if (cells.length !== target.length) return;
      cells.forEach((cell, i) => {
        cell.style.width = `${target[i]}%`;
      });
    });
    syncedTables.push({ table, target });
  });
  // fixed layout makes the colgroup authoritative on both sides
  if (syncedTables.length > 0) labelTable.style.tableLayout = 'fixed';

  // With exactly one synced item table, fold the header row INTO it as a
  // <thead>: two stacked tables draw two outer frames with a seam at the
  // junction in the Kendo redraw, while one table is one collapsed grid -
  // and Kendo repeatHeaders can then repeat the real column header on
  // physical page splits
  const labelRows = Array.from(labelTable.querySelectorAll('tr'));
  const single = syncedTables.length === 1 ? syncedTables[0] : null;
  if (
    single &&
    labelRows.length === 1 &&
    !single.table.querySelector('thead')
  ) {
    const headerRow = labelRows[0];
    if (single.target === mergedWidths) {
      // drop the blank spacer cells whose widths were merged away
      let seenReal = false;
      rowCells(headerRow).forEach(cell => {
        const isBlank =
          (cell.textContent || '').replace(/\u00a0/g, ' ').trim() === '';
        if (isBlank && seenReal) cell.remove();
        else seenReal = true;
      });
    }
    const headerCells = rowCells(headerRow);
    if (headerCells.length === single.target.length) {
      headerCells.forEach((cell, i) => {
        cell.style.width = `${single.target[i]}%`;
      });
      const thead = single.table.ownerDocument!.createElement('thead');
      thead.appendChild(headerRow);
      single.table.insertBefore(thead, single.table.querySelector('tbody'));
      labelTable.remove();
    }
  }
};

// CX-5430: complete half-declared cell borders on the item grid. A table
// border attribute or a style missing its width/style/color longhands is
// resolved by the browser to a soft inset default, but Kendo's PDF writer
// resolves it itself and draws dark solid lines (IAQ "black border"). Give
// every item/header-grid cell an explicit width+style+color instead.
export const completeItemTableBorders = (root: HTMLElement) => {
  const tables: HTMLTableElement[] = [];
  const push = (t: HTMLTableElement | null) => {
    if (t && tables.indexOf(t) === -1) tables.push(t);
  };
  push(resolveTable(root.querySelector('#ItemLabel')));
  root
    .querySelectorAll('[id="ItemValue"]')
    .forEach(el => push(resolveTable(el)));

  const SIDES = ['top', 'right', 'bottom', 'left'];
  tables.forEach(table => {
    const hadBorderAttr = table.hasAttribute('border');
    if (hadBorderAttr) table.removeAttribute('border');
    Array.from(table.querySelectorAll('th, td')).forEach(cellEl => {
      const s = (cellEl as HTMLTableCellElement).style;
      const anyDeclared = SIDES.some(
        side =>
          s.getPropertyValue(`border-${side}-width`) !== '' ||
          s.getPropertyValue(`border-${side}-style`) !== '' ||
          s.getPropertyValue(`border-${side}-color`) !== '',
      );
      if (!anyDeclared) {
        // no declaration at all - only the attribute border implied a grid
        if (hadBorderAttr) s.border = '1px solid #cccccc';
        return;
      }
      // complete per side so undeclared sides never gain a border (an
      // unset side width defaults to medium once a style appears)
      SIDES.forEach(side => {
        const width = s.getPropertyValue(`border-${side}-width`);
        const style = s.getPropertyValue(`border-${side}-style`);
        const hasWidth = width !== '' && parseFloat(width) !== 0;
        const hasStyle = style !== '' && style !== 'none' && style !== 'hidden';
        if (!hasWidth && !hasStyle) return;
        if (hasWidth && style === '') {
          s.setProperty(`border-${side}-style`, 'solid');
        }
        if (hasStyle && width === '') {
          s.setProperty(`border-${side}-width`, '1px');
        }
        if (s.getPropertyValue(`border-${side}-color`) === '') {
          s.setProperty(`border-${side}-color`, '#cccccc');
        }
      });
    });
    emulateCollapsedBorders(table);
    resolveTableFrame(table, hadBorderAttr);
  });
};

// The table element's OWN border (e.g. YESB: `border-style: solid;
// border-color` with the width coming from border="1") must not be left
// half-declared either: stripping the attribute would make the solid style
// fall back to the 3px `medium` default, and Kendo would draw the frame on
// top of the edge cells' borders. Resolve the frame the way collapse would
// - push it into the edge cells where they are thinner - then zero it on
// the table so exactly one engine-safe frame remains.
const resolveTableFrame = (
  table: HTMLTableElement,
  hadBorderAttr: boolean,
) => {
  const s = table.style;
  const declared = ['top', 'right', 'bottom', 'left'].some(
    side =>
      s.getPropertyValue(`border-${side}-width`) !== '' ||
      s.getPropertyValue(`border-${side}-style`) !== '',
  );
  if (!declared && !hadBorderAttr) return;

  const rows = Array.from(table.querySelectorAll('tr'))
    .map(tr => rowCells(tr))
    .filter(cells => cells.length > 0);
  if (rows.length === 0) return;

  const edgeCells = (side: string): HTMLTableCellElement[] => {
    if (side === 'top') return rows[0];
    if (side === 'bottom') return rows[rows.length - 1];
    return rows.map(cells =>
      side === 'left' ? cells[0] : cells[cells.length - 1],
    );
  };

  ['top', 'right', 'bottom', 'left'].forEach(side => {
    const width = s.getPropertyValue(`border-${side}-width`);
    const style = s.getPropertyValue(`border-${side}-style`);
    const hasStyle = style !== '' && style !== 'none' && style !== 'hidden';
    const intended =
      width !== ''
        ? hasStyle || hadBorderAttr
          ? borderWidthPx(width)
          : 0
        : hasStyle || hadBorderAttr
        ? 1
        : 0;
    if (intended > 0) {
      const frameColor =
        s.getPropertyValue(`border-${side}-color`) ||
        s.getPropertyValue('border-color') ||
        '#cccccc';
      edgeCells(side).forEach(cell => {
        const cs = cell.style;
        if (borderWidthPx(cs.getPropertyValue(`border-${side}-width`)) >= intended) {
          return;
        }
        cs.setProperty(`border-${side}-width`, `${intended}px`);
        const cellStyle = cs.getPropertyValue(`border-${side}-style`);
        if (cellStyle === '' || cellStyle === 'none') {
          cs.setProperty(`border-${side}-style`, 'solid');
        }
        if (cs.getPropertyValue(`border-${side}-color`) === '') {
          cs.setProperty(`border-${side}-color`, frameColor);
        }
      });
    }
  });
  s.setProperty('border-width', '0px');
};

// The browser merges adjacent cell borders under border-collapse: collapse,
// but Kendo's PDF writer draws every cell's borders separately and applies
// the default 2px border-spacing - each shared edge becomes TWO grey lines
// with a gap (IAQ "doubled border"). Emulate the collapse in the DOM:
// resolve each shared edge once, keep it on the first cell and zero the
// neighbour's side. The browser output is unchanged (collapse resolves to
// the same single line); Kendo is left with exactly one line per edge.
const borderWidthPx = (value: string): number => {
  if (value === 'thin') return 1;
  if (value === 'medium') return 3;
  if (value === 'thick') return 5;
  const n = parseFloat(value);
  return isFinite(n) ? n : 0;
};

const emulateCollapsedBorders = (table: HTMLTableElement) => {
  if (table.style.borderCollapse !== 'collapse') return;
  table.style.borderSpacing = '0';

  // keep the winning side on cell A, zero the same edge on cell B
  const resolveEdge = (
    a: HTMLTableCellElement,
    aSide: string,
    b: HTMLTableCellElement,
    bSide: string,
  ) => {
    const aw = borderWidthPx(a.style.getPropertyValue(`border-${aSide}-width`));
    const bw = borderWidthPx(b.style.getPropertyValue(`border-${bSide}-width`));
    if (aw === 0 && bw === 0) return;
    if (bw > aw) {
      a.style.setProperty(`border-${aSide}-width`, `${bw}px`);
      const bStyle = b.style.getPropertyValue(`border-${bSide}-style`);
      const bColor = b.style.getPropertyValue(`border-${bSide}-color`);
      if (bStyle) a.style.setProperty(`border-${aSide}-style`, bStyle);
      if (bColor) a.style.setProperty(`border-${aSide}-color`, bColor);
    }
    b.style.setProperty(`border-${bSide}-width`, '0px');
  };

  const rows = Array.from(table.querySelectorAll('tr')).map(tr =>
    rowCells(tr),
  );
  rows.forEach((cells, r) => {
    // vertical edges within the row
    for (let c = 1; c < cells.length; c++) {
      resolveEdge(cells[c - 1], 'right', cells[c], 'left');
    }
    // horizontal edges against the previous same-shaped row
    const prev = rows[r - 1];
    if (r > 0 && prev && prev.length === cells.length) {
      cells.forEach((cell, c) => resolveEdge(prev[c], 'bottom', cell, 'top'));
    }
  });
};

export const buildELetterDrawOptions = (templateWidthPx?: number) => ({
  paperSize: 'A4' as const,
  scale: getExportScale(templateWidthPx),
  margin: {
    top: PAGE_MARGIN,
    bottom: PAGE_MARGIN,
    left: PAGE_MARGIN,
    right: PAGE_MARGIN,
  },
  forcePageBreak: '.page-break',
  repeatHeaders: true,
});
